import React from 'react';

// A deploy replaces the content-hashed chunk files under /static/js. A browser
// that loaded the OLD index.html still asks for the OLD chunk hash on first
// open of a code-split drawer — which no longer exists, so the dynamic import()
// rejects with a ChunkLoadError ("Loading chunk 268 failed"). The Suspense
// boundary can't recover (the bytes are gone for good), so it bubbles to the
// nearest ErrorBoundary, reports to app_errors, and shows a dead "Try again"
// that re-requests the same missing hash. The only real fix is to reload the
// page once so the browser fetches the FRESH index.html (which references the
// new hashes). Guarded by a sessionStorage flag so a genuinely-broken build
// can't trap the tab in a reload loop — after one reload we let the error
// surface. The flag clears on any successful chunk load, so a later deploy can
// reload again.
export const CHUNK_RELOAD_KEY = 'lazyModal:chunkReloadedAt';

export function isChunkLoadError(err) {
  const name = err && err.name;
  const msg = (err && err.message) || '';
  return name === 'ChunkLoadError' || /Loading (CSS )?chunk [\w-]+ failed/i.test(msg);
}

export function withChunkReload(importFn) {
  return () => importFn().then(
    (mod) => {
      try { window.sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch { /* ignore */ }
      return mod;
    },
    (err) => {
      let alreadyReloaded = false;
      try { alreadyReloaded = !!window.sessionStorage.getItem(CHUNK_RELOAD_KEY); } catch { /* ignore */ }
      if (isChunkLoadError(err) && !alreadyReloaded) {
        try { window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now())); } catch { /* ignore */ }
        window.location.reload();
        // Never resolve — stop React rendering anything before the reload lands.
        return new Promise(() => {});
      }
      throw err;
    },
  );
}

// Code-split a heavy, conditionally-opened modal/drawer so it isn't downloaded
// or parsed in the initial bundle — it loads on first open, then is cached.
//
// MUST forwardRef. Some of these components (ComposeModal) expose an imperative
// handle the parent drives via a ref: composeRef.current.patchQuoteImages /
// snapshot / requestClose. A PLAIN function wrapper silently DROPS the ref
// (React logs "Function components cannot be given refs" and ref.current stays
// null forever) — which is exactly what broke inline images in replies after
// the modals were code-split: patchInlineQuoteWhenReady loaded the inline-image
// bytes but composeRef.current.patchQuoteImages was never callable, so the
// quote's cid: <img>s were never swapped to data: URIs. Forwarding the ref
// through Suspense to the lazy component fixes it; it's harmless for the modals
// that don't attach a ref.
export function lazyModal(importFn) {
  const C = React.lazy(withChunkReload(importFn));
  return React.forwardRef(function LazyModal(props, ref) {
    return (
      <React.Suspense fallback={null}>
        <C {...props} ref={ref} />
      </React.Suspense>
    );
  });
}
